import { useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Modal,
  Select,
  FormStack,
} from "@marekl/adminer/components";
import { notifyError, notifySuccess } from "@marekl/adminer/api";
import { useConfirm } from "@marekl/adminer/hooks";
import {
  useAssignRole,
  useRevokeRole,
  useUsers,
  type Assignment,
  type RoleScopeType,
  type SystemRole,
  type User,
} from "../../../auth/queries";
import { useFederations } from "../../../lib/queries/federations";
import { useCompetitions } from "../../../lib/queries/competitions";
import { ROLE_LABEL, SCOPE_LABEL } from "../../../lib/i18n/roles";
import styles from "./UsersPage.module.css";
import type { ID } from "../../../lib/id";

// Stored (assignable) roles only — virtual roles (team_leader, coach,
// assistant, athlete) are derived and managed via TeamLeadership /
// TeamMembership flows.
const ASSIGNABLE_ROLES: { value: SystemRole; label: string; scope: RoleScopeType }[] = [
  { value: "admin", label: ROLE_LABEL.admin, scope: "global" },
  { value: "members_admin", label: ROLE_LABEL.members_admin, scope: "global" },
  { value: "members_viewer", label: ROLE_LABEL.members_viewer, scope: "global" },
  { value: "competitions_admin", label: ROLE_LABEL.competitions_admin, scope: "global" },
  { value: "competitions_viewer", label: ROLE_LABEL.competitions_viewer, scope: "global" },
  { value: "global_viewer", label: ROLE_LABEL.global_viewer, scope: "global" },
  { value: "judge", label: ROLE_LABEL.judge, scope: "global" },
  { value: "custom_pages_manager", label: ROLE_LABEL.custom_pages_manager, scope: "global" },
  { value: "notices_manager", label: ROLE_LABEL.notices_manager, scope: "global" },
  { value: "federation_admin", label: ROLE_LABEL.federation_admin, scope: "federation" },
  { value: "competition_organizer", label: ROLE_LABEL.competition_organizer, scope: "competition" },
];

function assignmentText(a: Assignment, scopeName?: string): string {
  const role = ROLE_LABEL[a.role] ?? a.role;
  if (a.scope_type === "global") return role;
  const name = scopeName ?? a.scope_name;
  return name ? `${role} · ${name}` : `${role} (${SCOPE_LABEL[a.scope_type]})`;
}

export function ManageRolesModal({ user, onClose }: { user: User; onClose: () => void }) {
  const confirm = useConfirm();
  const assign = useAssignRole(user.id);
  const revoke = useRevokeRole(user.id);
  const { data: federations = [] } = useFederations();
  const { data: competitions = [] } = useCompetitions();
  const { data: users } = useUsers();
  const liveUser = users?.find((u) => u.id === user.id) ?? user;

  const [role, setRole] = useState<SystemRole>("members_admin");
  const def = ASSIGNABLE_ROLES.find((r) => r.value === role);
  const scopeType: RoleScopeType = def?.scope ?? "global";
  const [federationId, setFederationId] = useState<ID | "">("");
  const [competitionId, setCompetitionId] = useState<ID | "">("");

  useEffect(() => {
    if (scopeType === "federation" && federationId === "" && federations.length > 0) {
      setFederationId(federations[0].id);
    }
  }, [scopeType, federationId, federations]);

  function scopeName(a: Assignment): string | undefined {
    if (a.scope_id == null) return undefined;
    if (a.scope_type === "federation") {
      return federations.find((f) => f.id === a.scope_id)?.name;
    }
    if (a.scope_type === "competition") {
      return competitions.find((c) => c.id === a.scope_id)?.name;
    }
    return undefined;
  }

  async function handleAssign() {
    try {
      let scopeId: ID | null = null;
      if (scopeType === "federation") {
        scopeId = federationId === "" ? null : federationId;
      } else if (scopeType === "competition") {
        scopeId = competitionId === "" ? null : competitionId;
      }
      const payload = {
        role,
        scope_type: scopeType,
        scope_id: scopeId,
      };
      await assign.mutateAsync(payload);
      notifySuccess("Role byla přidělena.");
    } catch (err) {
      notifyError(err);
    }
  }

  // The confirm host renders its dialog outside this one, so a Modal no
  // longer nests inside a Modal — and with 0.23.0's layered Escape, one
  // keystroke closes the confirm without taking the roles dialog with it.
  function askRevoke(a: Assignment) {
    const id = a.id;
    if (!id) return;
    confirm({
      title: "Odebrat roli",
      description: (
        <>
          Opravdu odebrat roli <strong>{assignmentText(a, scopeName(a))}</strong>?
        </>
      ),
      confirmLabel: "Odebrat",
      variant: "danger",
      onConfirm: async () => {
        await revoke.mutateAsync(id);
        notifySuccess("Role byla odebrána.");
      },
    });
  }

  return (
    <Modal
      open
      title={`Role: ${liveUser.email}`}
      onClose={onClose}
      width="md"
      footer={<Button onClick={onClose}>Zavřít</Button>}
    >
      <FormStack>
        {liveUser.assignments.length === 0 ? (
          <Alert variant="info">Uživatel nemá žádné role.</Alert>
        ) : (
          <div>
            {liveUser.assignments.map((a, i) => (
              <div key={i} className={styles.assignmentRow}>
                <div className={styles.assignmentLabel}>
                  <span>{assignmentText(a, scopeName(a))}</span>
                  {a.virtual ? (
                    <span className={styles.assignmentHint}>
                      odvozeno z členství / vedení týmu
                    </span>
                  ) : null}
                </div>
                {a.virtual ? (
                  <Badge variant="muted">Odvozená</Badge>
                ) : (
                  <Button weight="ghost" onClick={() => askRevoke(a)}>
                    Odebrat
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}

        <hr />

        <h4 style={{ margin: 0 }}>Přidat roli</h4>
        <Select
          id="assign-role"
          label="Role"
          required
          value={role}
          onChange={(e) => setRole(e.target.value as SystemRole)}
        >
          {ASSIGNABLE_ROLES.map((r) => (
            <option key={r.value} value={r.value}>{r.label}</option>
          ))}
        </Select>
        {scopeType === "federation" ? (
          <Select
            id="assign-federation"
            label="Federace"
            required
            value={String(federationId)}
            onChange={(e) =>
              setFederationId(e.target.value)
            }
          >
            <option value="">— vyberte —</option>
            {federations.map((f) => (
              <option key={f.id} value={String(f.id)}>{f.name}</option>
            ))}
          </Select>
        ) : null}
        {scopeType === "competition" ? (
          <Select
            id="assign-competition"
            label="Soutěž"
            required
            value={String(competitionId)}
            onChange={(e) =>
              setCompetitionId(e.target.value)
            }
          >
            <option value="">— vyberte —</option>
            {competitions.map((c) => (
              <option key={c.id} value={String(c.id)}>{c.name}</option>
            ))}
          </Select>
        ) : null}
        <Alert variant="info">
          Role <strong>vedoucí</strong>, <strong>trenér</strong>, <strong>asistent</strong> a{" "}
          <strong>soutěžící</strong> se nepřidělují zde; odvozují se z členství v týmu.
        </Alert>
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="primary"
            onClick={handleAssign}
            loading={assign.isPending}
            disabled={
              (scopeType === "federation" && federationId === "") ||
              (scopeType === "competition" && competitionId === "")
            }
          >
            Přidělit
          </Button>
        </div>
      </FormStack>
    </Modal>
  );
}
